import React, { FC } from 'react'
import { Card, CardContent, Typography, Box, Chip, Button } from '@mui/material'
import {
  Sms,
  Email,
  Smartphone,
  Delete,
  CheckCircle,
  Pending,
} from '@mui/icons-material'
import {
  MfaFactor,
  getMaskedIdentifier,
  getFactorIdentifier,
} from 'models/Mfa.d'
import useStyles from './factor-card-styles'

interface FactorCardProps {
  factor: MfaFactor
  onRemove?: (factorId: string) => void
  onActivate?: (factorId: string) => void
  showActions?: boolean
  showFullDetails?: boolean
}

const getFactorIcon = (type: string) => {
  switch (type) {
    case 'sms':
      return <Sms />
    case 'email':
      return <Email />
    case 'totp':
      return <Smartphone />
    default:
      return null
  }
}

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return <CheckCircle color="success" fontSize="small" />
    case 'PENDING_ACTIVATION':
      return <Pending color="warning" fontSize="small" />
    default:
      return null
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'Active'
    case 'PENDING_ACTIVATION':
      return 'Pending Activation'
    case 'INACTIVE':
      return 'Inactive'
    default:
      return status
  }
}

const getFactorDisplayName = (type: string): string => {
  switch (type) {
    case 'sms':
      return 'SMS'
    case 'email':
      return 'Email'
    case 'totp':
      return 'Authenticator App'
    default:
      return type
  }
}

const FactorCard: FC<FactorCardProps> = ({
  factor,
  onRemove,
  onActivate,
  showActions = true,
  showFullDetails = false,
}) => {
  const { classes } = useStyles()

  const handleRemove = () => {
    if (onRemove) {
      onRemove(factor.id)
    }
  }

  const handleActivate = () => {
    if (onActivate) {
      onActivate(factor.id)
    }
  }

  const isPending = factor.status === 'PENDING_ACTIVATION'
  const displayIdentifier = showFullDetails
    ? getFactorIdentifier(factor)
    : getMaskedIdentifier(factor)

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box className={classes.header}>
          <Box className={classes.iconAndTitle}>
            <Box className={classes.icon}>{getFactorIcon(factor.type)}</Box>
            <Box>
              <Typography variant="h6" className={classes.title}>
                {getFactorDisplayName(factor.type)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {displayIdentifier}
              </Typography>
            </Box>
          </Box>
          <Box className={classes.statusContainer}>
            {getStatusIcon(factor.status)}
            <Typography variant="body2" className={classes.statusText}>
              {getStatusLabel(factor.status)}
            </Typography>
          </Box>
        </Box>

        <Box className={classes.content}>
          {factor.isRequired && (
            <Chip
              label="Required"
              size="small"
              color="primary"
              className={classes.requiredChip}
            />
          )}
          {factor.lastUsedAt && (
            <Typography variant="caption" color="textSecondary">
              Last used: {new Date(factor.lastUsedAt).toLocaleDateString()}
            </Typography>
          )}
        </Box>

        {showActions &&
          ((isPending && onActivate) ||
            (onRemove && factor.type !== 'email')) && (
            <Box className={classes.actions}>
              {isPending && onActivate && (
                <Button
                  variant="text"
                  size="small"
                  onClick={handleActivate}
                  className={classes.activateButton}
                >
                  Complete Activation
                </Button>
              )}
              {onRemove && factor.type !== 'email' && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<Delete fontSize="small" />}
                  onClick={handleRemove}
                  className={classes.deleteButton}
                >
                  Delete
                </Button>
              )}
            </Box>
          )}
      </CardContent>
    </Card>
  )
}

export default FactorCard
